import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import RootRedirect from './components/RootRedirect';
import ProtectedRoute from './components/auth/ProtectedRoute';
import LoginForm from './components/auth/LoginForm';
import MagicLinkVerify from './pages/MagicLinkVerify';
import SignUp from './pages/SignUp';
import PlatformPlatform from './platform/pages/Platform';
import PlatformFootprint from './platform/pages/Footprint';
import PlatformSiteBuilder from './platform/pages/SiteBuilder';
import PlatformPricing from './platform/pages/Pricing';
import PlatformAbout from './platform/pages/About';
import DonationsSettings from './pages/settings/DonationsSettings';
import StatesSettings from './pages/settings/StatesSettings';
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnersSettings from './pages/settings/PartnersSettings';
import PartnerDetail from './pages/settings/PartnerDetail';
import PartnerFormBuilder from './pages/settings/PartnerFormBuilder';
import StateContentBuilder from './pages/settings/StateContentBuilder';
import StateContentEditor from './pages/settings/StateContentEditor';
import SiteSettings from './pages/settings/SiteSettings';
import TenantPage from './components/TenantPage';
import SuperuserDashboard from './pages/SuperuserDashboard';
import DonateSuccess from './pages/DonateSuccess';

// Provider nesting mirrors grant_management: react-query wraps auth wraps router,
// so the getBranding / current_user queries and the auth token reach every route.
// "/" (and any unmatched path) dispatches via RootRedirect, whose platform-vs-
// tenant decision is server-driven, not host-parsed. The admin console lives
// under /settings behind ProtectedRoute.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: false, refetchOnWindowFocus: false } },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/auth/verify" element={<MagicLinkVerify />} />
            {/* SGOKit platform marketing site (platform domain). These are
                server-neutral static pages; RootRedirect still governs "/". */}
            <Route path="/signup" element={<SignUp />} />
            <Route path="/platform" element={<PlatformPlatform />} />
            <Route path="/footprint" element={<PlatformFootprint />} />
            <Route path="/site" element={<PlatformSiteBuilder />} />
            <Route path="/pricing" element={<PlatformPricing />} />
            <Route path="/about" element={<PlatformAbout />} />
            {/* Public, tenant-scoped pages. Per-state pages, /apply, /donate,
                and /partners/join all live under the CMS now (per-state pages
                via the PageTemplate route /pages/state/<abbr>/, the other
                three as System Pages), handled by the catch-all TenantPage
                route below. /donate/success is the one exception — a Stripe
                `return_url` target, not a page, so it has no CMS equivalent. */}
            <Route path="/donate/success" element={<DonateSuccess />} />
            {/* Admin console (org admins only). */}
            {/* A school's own console (partner accounts only) — the sibling of
                the admin console below, not a CMS page. */}
            <Route
              path="/partners/dashboard"
              element={
                <ProtectedRoute requirePartner>
                  <PartnerDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/donations"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <DonationsSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/states"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <StatesSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/states/content"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <StateContentBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/states/content/manifests/:manifestId"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <StateContentBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/states/:code/content"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <StateContentEditor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/partners"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <PartnersSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/partners/form"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <PartnerFormBuilder />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/partners/:id"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <PartnerDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/site"
              element={
                <ProtectedRoute requireOrgAdmin>
                  <SiteSettings />
                </ProtectedRoute>
              }
            />
            {/* SGOKit platform console (system admins only). Not /admin — that's
                Django admin server-side. Lives on the platform domain, where
                system admins (who have no organization) sign in. */}
            <Route
              path="/superadmin"
              element={
                <ProtectedRoute requireSuperuser>
                  <SuperuserDashboard />
                </ProtectedRoute>
              }
            />
            {/* Everything else is a CMS page path (or the platform marketing
                fallback): TenantPage renders it without bouncing admins to the
                console, so they can view and inline-edit pages. */}
            <Route path="*" element={<TenantPage />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
